import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Download,
  Eye,
  FileText,
  Image as ImageIcon,
  Loader2,
  Paperclip,
  Plus,
  Trash2,
  Video,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  documentRequestsApi,
  type DocumentRequestAttachment,
  type DocumentRequestAuditLog,
} from '@/api/document-requests';

export const DOCUMENT_REQUEST_ATTACHMENT_ACCEPT = [
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.pdf',
  '.jpg',
  '.jpeg',
  '.png',
  '.webp',
  '.gif',
  '.bmp',
  '.tif',
  '.tiff',
  '.heic',
  '.heif',
  '.webm',
].join(',');

function formatSize(size: number): string {
  if (size < 1024) return `${size} Б`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} КБ`;
  return `${(size / (1024 * 1024)).toFixed(1)} МБ`;
}

function formatDate(value?: string | null): string {
  if (!value) return '';
  return new Date(value).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function attachmentIcon(attachment: Pick<DocumentRequestAttachment, 'kind'>) {
  if (attachment.kind === 'IMAGE') return <ImageIcon className="h-4 w-4 text-blue-500" />;
  if (attachment.kind === 'VIDEO') return <Video className="h-4 w-4 text-purple-500" />;
  return <FileText className="h-4 w-4 text-muted-foreground" />;
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

function usePreviewUrl(requestId: string | null, attachment: DocumentRequestAttachment | null) {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    let objectUrl: string | null = null;
    setUrl(null);
    setError(null);

    if (!requestId || !attachment?.canPreview) {
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    documentRequestsApi.previewAttachment(requestId, attachment.id)
      .then((blob) => {
        if (!alive) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {
        if (alive) setError('Ошибка предпросмотра');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [requestId, attachment?.id, attachment?.canPreview]);

  return { url, loading, error };
}

function AttachmentPreviewDialog({
  requestId,
  attachment,
  onOpenChange,
}: {
  requestId: string | null;
  attachment: DocumentRequestAttachment | null;
  onOpenChange: (open: boolean) => void;
}) {
  const { url, loading, error } = usePreviewUrl(requestId, attachment);

  return (
    <Dialog open={Boolean(attachment)} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] w-[calc(100vw-24px)] max-w-3xl p-4">
        <DialogHeader>
          <DialogTitle className="pr-8 text-base">{attachment?.originalName || 'Предпросмотр'}</DialogTitle>
        </DialogHeader>
        <div className="flex min-h-[320px] items-center justify-center overflow-auto rounded-lg border bg-muted">
          {loading && <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />}
          {error && <div className="p-4 text-sm text-destructive">{error}</div>}
          {!loading && !error && url && attachment?.kind === 'IMAGE' && (
            <img src={url} alt={attachment.originalName} className="max-h-[70vh] max-w-full object-contain" />
          )}
          {!loading && !error && url && attachment?.kind === 'VIDEO' && (
            <video src={url} controls className="max-h-[70vh] max-w-full" />
          )}
          {!loading && !error && url && attachment?.mimeType === 'application/pdf' && (
            <iframe src={url} title={attachment.originalName} className="h-[70vh] w-full bg-background" />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AttachmentRow({
  requestId,
  attachment,
  onPreview,
  onDeleted,
}: {
  requestId: string;
  attachment: DocumentRequestAttachment;
  onPreview: (attachment: DocumentRequestAttachment) => void;
  onDeleted?: () => void;
}) {
  const [busy, setBusy] = useState<'download' | 'delete' | null>(null);

  const download = async () => {
    setBusy('download');
    try {
      const blob = await documentRequestsApi.downloadAttachment(requestId, attachment.id);
      downloadBlob(blob, attachment.originalName);
    } catch {
      toast.error('Ошибка скачивания');
    } finally {
      setBusy(null);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Удалить вложение "${attachment.originalName}"?`)) return;
    setBusy('delete');
    try {
      await documentRequestsApi.deleteAttachment(requestId, attachment.id);
      toast.success('Вложение удалено');
      onDeleted?.();
    } catch {
      toast.error('Ошибка удаления');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      data-testid="document-request-attachment-row"
      data-attachment-name={attachment.originalName}
      className="flex items-center gap-2 rounded-lg border bg-card p-2"
    >
      <div className="shrink-0">{attachmentIcon(attachment)}</div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">{attachment.originalName}</div>
        <div className="text-xs text-muted-foreground">
          {formatSize(attachment.sizeBytes)} · {attachment.uploadedBy?.name || '—'} · {formatDate(attachment.createdAt)}
          {attachment.deletedAt && <span className="text-destructive"> · удалено {formatDate(attachment.deletedAt)}</span>}
        </div>
      </div>
      <div className="flex shrink-0 gap-1">
        {attachment.canPreview && (
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => onPreview(attachment)}>
            <Eye className="h-4 w-4" />
          </Button>
        )}
        <Button variant="outline" size="icon" className="h-8 w-8" onClick={download} disabled={busy === 'download'}>
          {busy === 'download' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
        </Button>
        {attachment.canDelete && (
          <Button variant="outline" size="icon" className="h-8 w-8 text-destructive" onClick={remove} disabled={busy === 'delete'}>
            {busy === 'delete' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          </Button>
        )}
      </div>
    </div>
  );
}

export function DocumentRequestAttachments({
  requestId,
  readOnly = false,
}: {
  requestId: string;
  readOnly?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState<DocumentRequestAttachment | null>(null);
  const query = useQuery({
    queryKey: ['document-request-attachments', requestId],
    queryFn: () => documentRequestsApi.getAttachments(requestId),
  });
  const attachments = query.data?.items ?? [];

  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['document-request-attachments', requestId] }),
      queryClient.invalidateQueries({ queryKey: ['document-request-history', requestId] }),
    ]);
  };

  const upload = async (files: FileList | File[]) => {
    const selected = Array.from(files);
    if (selected.length === 0) return;
    setUploading(true);
    try {
      const result = await documentRequestsApi.uploadAttachments(requestId, selected, 'MOBILE');
      if (result.uploaded.length > 0) toast.success(`Загружено: ${result.uploaded.length}`);
      result.errors.forEach((item) => toast.error(`${item.filename}: ${item.message}`));
      await invalidate();
    } catch {
      toast.error('Ошибка загрузки вложений');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="flex items-center gap-2 text-sm font-medium">
          <Paperclip className="h-4 w-4" />
          Вложения
        </h4>
        {!readOnly && (
          <>
            <input
              ref={inputRef}
              type="file"
              multiple
              accept={DOCUMENT_REQUEST_ATTACHMENT_ACCEPT}
              className="hidden"
              onChange={(event) => event.target.files && upload(event.target.files)}
            />
            <Button size="sm" variant="outline" onClick={() => inputRef.current?.click()} disabled={uploading}>
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            </Button>
          </>
        )}
      </div>

      {query.isLoading ? (
        <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Загрузка вложений
        </div>
      ) : query.error ? (
        <div className="text-sm text-destructive">Ошибка загрузки вложений</div>
      ) : attachments.length === 0 ? (
        <div className="text-sm text-muted-foreground">Файлов нет</div>
      ) : (
        <div className="space-y-2">
          {attachments.map((attachment) => (
            <AttachmentRow
              key={attachment.id}
              requestId={requestId}
              attachment={attachment}
              onPreview={setPreviewAttachment}
              onDeleted={invalidate}
            />
          ))}
        </div>
      )}

      <AttachmentPreviewDialog
        requestId={requestId}
        attachment={previewAttachment}
        onOpenChange={(open) => !open && setPreviewAttachment(null)}
      />
    </section>
  );
}

function attachmentFromAudit(item: DocumentRequestAuditLog): DocumentRequestAttachment | null {
  if (item.attachment) return item.attachment;
  const snapshot = item.dataAfter || item.dataBefore;
  if (!snapshot || typeof snapshot !== 'object') return null;
  const extension = typeof snapshot.extension === 'string' ? snapshot.extension : '';
  return {
    id: typeof snapshot.id === 'string' ? snapshot.id : item.recordId,
    requestId: typeof snapshot.requestId === 'string' ? snapshot.requestId : '',
    originalName: typeof snapshot.originalName === 'string' ? snapshot.originalName : 'Файл',
    mimeType: typeof snapshot.mimeType === 'string' ? snapshot.mimeType : 'application/octet-stream',
    extension,
    sizeBytes: typeof snapshot.sizeBytes === 'number' ? snapshot.sizeBytes : 0,
    sha256: typeof snapshot.sha256 === 'string' ? snapshot.sha256 : '',
    kind: snapshot.kind === 'IMAGE' || snapshot.kind === 'VIDEO' ? snapshot.kind : 'DOCUMENT',
    sourceApp: snapshot.sourceApp === 'MOBILE' ? 'MOBILE' : 'DESKTOP',
    uploadedBy: null,
    deletedAt: typeof snapshot.deletedAt === 'string' ? snapshot.deletedAt : null,
    deletedBy: null,
    createdAt: typeof snapshot.createdAt === 'string' ? snapshot.createdAt : item.createdAt,
    canPreview: ['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'pdf', 'webm'].includes(extension),
    canDownload: true,
    canDelete: false,
  };
}

export function useDocumentRequestAttachmentHistoryRenderer(requestId: string) {
  const [previewAttachment, setPreviewAttachment] = useState<DocumentRequestAttachment | null>(null);
  const renderer = useMemo(() => {
    return (item: DocumentRequestAuditLog): ReactNode | null => {
      if (item.modelName !== 'DocumentRequestAttachment') return null;
      const attachment = attachmentFromAudit(item);
      if (!attachment) return null;
      const deleted = item.action === 'UPDATE' && Boolean(item.diff?.deletedAt);
      const title = item.action === 'CREATE'
        ? 'Добавлен файл'
        : deleted
          ? 'Удален файл'
          : 'Изменение вложения';

      return (
        <div className="overflow-hidden rounded-lg border border-border">
          <div className="bg-muted px-3 py-2 text-xs font-medium">
            {title} · {formatDate(item.createdAt)}
            {item.userName ? ` · ${item.userName}` : ''}
          </div>
          <div className="p-2">
            <AttachmentRow
              requestId={requestId}
              attachment={{ ...attachment, canDelete: false }}
              onPreview={setPreviewAttachment}
            />
          </div>
        </div>
      );
    };
  }, [requestId]);

  const preview = (
    <AttachmentPreviewDialog
      requestId={requestId}
      attachment={previewAttachment}
      onOpenChange={(open) => !open && setPreviewAttachment(null)}
    />
  );

  return { renderer, preview };
}
